import React, { useEffect, useState } from "react";
import GenericTable from "../GenericTable/GenericTable";
import { useNavigate } from "react-router-dom";
import { CircularProgress, Button, Alert, Box, IconButton, Typography } from "@mui/material";
import config from "../../../config";

const syncColumns = [
  { id: "store_name", label: "Store Name" },
  { id: "pos_name", label: "POS System" },
  { id: "last_sync_date", label: "Last Sync Date" },
  { id: "sync_status", label: "Status" },
  {
    id: "sync_button",
    label: "Sync"
  }
];



function SyncList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [storeList, setStoreList] = useState([]);
  const [error, setError] = useState(null);
  const [syncLoading, setSyncLoading] = useState({});
  const [syncMessage, setSyncMessage] = useState(null);
  const [anySyncInProgress, setAnySyncInProgress] = useState(false); // Global sync lock
  const [currentSyncingStore, setCurrentSyncingStore] = useState(null); // Track which store is syncing

  // ✅ Function to refresh notifications after successful sync
  const refreshNotifications = async () => {
    try {
      console.log("🔔 Refreshing notifications after successful sync...");
      const clientId = config.clientId;
      const apiKey = config.apiKey;

      if (!clientId || !apiKey) {
        console.warn("⚠️ Cannot refresh notifications: Missing clientId or apiKey");
        return;
      }

      // Trigger a custom event that the TopNavbar Notifications component can listen to
      window.dispatchEvent(new CustomEvent('refreshNotifications'));

      console.log("✅ Notification refresh event dispatched");
    } catch (error) {
      console.error("❌ Error refreshing notifications:", error);
    }
  };

  // Custom Sync Button Component
  const SyncButton = ({ store }) => {
    const isLoading = syncLoading[store.client_store_id];
    const isThisStoreSyncing = currentSyncingStore === store.client_store_id;
    const isAnotherStoreSyncing = anySyncInProgress && !isThisStoreSyncing;

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} color="primary" />
          <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
            Syncing...
          </Typography>
        </Box>
      );
    }

    return (
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={() => handleSyncProducts(store)}
        disabled={isAnotherStoreSyncing} // Disable if another store is syncing
        startIcon={<i className="material-symbols-outlined" style={{ fontSize: 16 }}>sync</i>}
        sx={{
          minWidth: '100px',
          borderRadius: '20px',
          textTransform: 'none',
          fontWeight: 'bold',
          fontSize: '0.75rem'
        }}
      >
      </Button>
    );
  };

  // Fetch configured stores function (moved outside useEffect so it can be reused)
  const fetchConfiguredStores = async () => {
    try {
      setLoading(true);
      const clientId = config.clientId ;

        const response = await fetch(`${config.API_BASE_URL}/client/pos/configured-stores?client_id=${clientId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json"
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch configured stores: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          // Add actions to each store (sync button will be rendered dynamically)
          const storesWithActions = (result.data || []).map(store => {
            // Format last_sync_date if it exists
            let formattedSyncDate = 'N/A';
            if (store.last_sync_date) {
              const date = new Date(store.last_sync_date);
              const dateStr = date.toLocaleDateString();
              const timeStr = date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
              });
              formattedSyncDate = `${dateStr} ${timeStr}`;
            }

            return {
              ...store,
              last_sync_date: formattedSyncDate,
              actions: store.client_store_id, // Store ID for actions
              sync_button: store.client_store_id // Store ID for sync button
            };
          });
          setStoreList(storesWithActions);

          // Check if there's a pending sync from POSConfigure
          const pendingSyncData = localStorage.getItem("pendingSync");
          if (pendingSyncData) {
            try {
              const { storeId, shouldSync, timestamp } = JSON.parse(pendingSyncData);

              // Check if sync was triggered within last 5 minutes (to avoid stale data)
              const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
              if (timestamp > fiveMinutesAgo && shouldSync) {
                console.log("🔄 Detected pending sync request for store:", storeId);

                // Find the store in the list
                const storeToSync = storesWithActions.find(s => s.client_store_id === storeId);
                if (storeToSync) {
                  // Set global sync lock
                  setAnySyncInProgress(true);
                  setCurrentSyncingStore(storeId);

                  // Show loading state immediately
                  setSyncLoading(prev => ({ ...prev, [storeId]: true }));
                  setSyncMessage({
                    type: 'info',
                    text: `🔄 Starting sync for ${storeToSync.store_name}... Please wait.`
                  });

                  // Call batch sync API and start polling
                  triggerBatchSync(storeId, storeToSync.store_name);
                } else {
                  console.warn("⚠️ Store not found in list:", storeId);
                  localStorage.removeItem("pendingSync");
                }
              } else {
                // Stale data, clear it
                console.log("🗑️ Clearing stale pending sync data");
                localStorage.removeItem("pendingSync");
              }
            } catch (e) {
              console.error("Error parsing pending sync data:", e);
              localStorage.removeItem("pendingSync");
            }
          }

          // Also check if there's an active sync in progress (in case of page refresh)
          const activeSyncData = localStorage.getItem("activeSyncStore");
          if (activeSyncData) {
            try {
              const { storeId, storeName, timestamp } = JSON.parse(activeSyncData);

              // Check if sync was triggered within last 10 minutes
              const tenMinutesAgo = Date.now() - (10 * 60 * 1000);
              if (timestamp > tenMinutesAgo) {
                console.log("🔄 Detected active sync in progress for store:", storeName);

                // Set global sync lock
                setAnySyncInProgress(true);
                setCurrentSyncingStore(storeId);

                // Show loading state
                setSyncLoading(prev => ({ ...prev, [storeId]: true }));
                setSyncMessage({
                  type: 'info',
                  text: `🔄 Sync in progress for ${storeName}... Please wait.`
                });
              } else {
                // Stale data, clear it
                console.log("🗑️ Clearing stale active sync data");
                localStorage.removeItem("activeSyncStore");
              }
            } catch (e) {
              console.error("Error parsing active sync data:", e);
              localStorage.removeItem("activeSyncStore");
            }
          }
        } else {
          throw new Error(result.message || "Failed to fetch stores");
        }

    } catch (err) {
      console.error("Failed to fetch configured stores:", err);
      setError(err.message);
      setStoreList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfiguredStores();
  }, []);

  // Trigger batch sync API and start polling
  const triggerBatchSync = async (storeId, storeName) => {
    try {
      console.log("🚀 Calling batch sync API for store:", storeId);

      // Call batch sync API with query parameters
      const url = new URL(`${config.API_BASE_URL}/batch/sync/store`);
      url.searchParams.append('client_store_id', storeId);
      url.searchParams.append('batch_type', 'both');

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Failed to start sync");
      }

      console.log("✅ Batch sync API called successfully. Batch ID:", result.batch_run_id);

      // Update message
      setSyncMessage({
        type: 'info',
        text: `🔄 Syncing ${storeName}... This may take a few minutes.`
      });

      // Start polling for status
      if (result.batch_run_id) {
        pollBatchStatus(result.batch_run_id, storeId, storeName);
      } else {
        throw new Error("No batch_run_id returned from API");
      }

    } catch (error) {
      console.error("❌ Failed to trigger batch sync:", error);
      setSyncLoading(prev => ({ ...prev, [storeId]: false }));
      setSyncMessage({
        type: 'error',
        text: `❌ Failed to start sync for ${storeName}: ${error.message}`
      });
      // Release global sync lock
      setAnySyncInProgress(false);
      setCurrentSyncingStore(null);
      // Clear localStorage on error
      localStorage.removeItem("pendingSync");
      localStorage.removeItem("activeSyncStore");
    }
  };

  // Poll batch status until completion
  const pollBatchStatus = async (batchRunId, storeId, storeName) => {
    const maxAttempts = 60; // Poll for max 5 minutes (60 * 5 seconds)
    let attempts = 0;

    const poll = async () => {
      try {
        attempts++;
        console.log(`📊 Polling batch status (attempt ${attempts}/${maxAttempts})... Batch ID: ${batchRunId}`);

        const response = await fetch(`${config.API_BASE_URL}/batch/status/${batchRunId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json"
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch batch status: ${response.status}`);
        }

        const result = await response.json();
        console.log("📊 Batch status response:", result);

        if (result.success && result.data) {
          const status = result.data.status;
          console.log(`📊 Current status: ${status}`);

          if (status === 'finished') {
            // Sync completed successfully
            console.log("✅ Batch sync completed!");
            setSyncLoading(prev => ({ ...prev, [storeId]: false }));
            setSyncMessage({
              type: 'success',
              text: `✅ Sync completed for ${storeName}!`
            });
            // Release global sync lock
            setAnySyncInProgress(false);
            setCurrentSyncingStore(null);
            // Clear both localStorage keys
            localStorage.removeItem("pendingSync");
            localStorage.removeItem("activeSyncStore");
            // Refresh store list to show updated last sync time
            fetchConfiguredStores();

            // ✅ Refresh notifications immediately after successful sync
            refreshNotifications();

            return; // Stop polling
          } else if (status === 'failed') {
            // Sync failed
            console.error("❌ Batch sync failed!");
            setSyncLoading(prev => ({ ...prev, [storeId]: false }));
            setSyncMessage({
              type: 'error',
              text: `❌ Sync failed for ${storeName}. Please try again.`
            });
            // Release global sync lock
            setAnySyncInProgress(false);
            setCurrentSyncingStore(null);
            // Clear both localStorage keys
            localStorage.removeItem("pendingSync");
            localStorage.removeItem("activeSyncStore");
            return; // Stop polling
          } else if (status === 'running' || status === 'pending') {
            // Still running, continue polling
            console.log(`⏳ Status is ${status}, continuing to poll...`);
            if (attempts < maxAttempts) {
              setTimeout(poll, 5000); // Poll every 5 seconds
            } else {
              // Max attempts reached
              console.warn("⚠️ Max polling attempts reached");
              setSyncLoading(prev => ({ ...prev, [storeId]: false }));
              setSyncMessage({
                type: 'warning',
                text: `⚠️ Sync is taking longer than expected for ${storeName}. Please check back later.`
              });
              // Release global sync lock
              setAnySyncInProgress(false);
              setCurrentSyncingStore(null);
              // Clear both localStorage keys
              localStorage.removeItem("pendingSync");
              localStorage.removeItem("activeSyncStore");
            }
          }
        } else {
          throw new Error("Invalid batch status response");
        }
      } catch (error) {
        console.error("❌ Error polling batch status:", error);
        setSyncLoading(prev => ({ ...prev, [storeId]: false }));
        setSyncMessage({
          type: 'error',
          text: `❌ Failed to check sync status for ${storeName}.`
        });
        // Release global sync lock
        setAnySyncInProgress(false);
        setCurrentSyncingStore(null);
        // Clear both localStorage keys on error
        localStorage.removeItem("pendingSync");
        localStorage.removeItem("activeSyncStore");
      }
    };

    // Start polling immediately
    poll();
  };

  const handleSyncProducts = async (store) => {
    const storeId = store.client_store_id;
    const storeName = store.store_name;

    // Check if any sync is already in progress
    if (anySyncInProgress) {
      console.warn("⚠️ Another sync is already in progress. Please wait.");
      setSyncMessage({
        type: 'warning',
        text: `⚠️ Another store is currently syncing. Please wait for it to complete.`
      });
      return; // Don't start a new sync
    }

    console.log("🚀 User clicked sync for store:", storeName);

    // Set global sync lock
    setAnySyncInProgress(true);
    setCurrentSyncingStore(storeId);

    // Store sync info in localStorage before calling API
    localStorage.setItem("activeSyncStore", JSON.stringify({
      storeId: storeId,
      storeName: storeName,
      timestamp: Date.now()
    }));

    // Show loading state immediately
    setSyncLoading(prev => ({ ...prev, [storeId]: true }));
    setSyncMessage({
      type: 'info',
      text: `🔄 Starting sync for ${storeName}... Please wait.`
    });

    // Call batch sync API and start polling
    triggerBatchSync(storeId, storeName);
  };



  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        Error loading stores: {error}
      </div>
    );
  }

  return (
    <>
      {syncMessage && (
        <Alert
          severity={syncMessage.type}
          sx={{ mb: 2 }}
          onClose={() => setSyncMessage(null)}
        >
          {syncMessage.text}
        </Alert>
      )}

      <GenericTable
        title="POS Management"
        columns={syncColumns}
        rows={storeList.map(store => ({
          ...store,
          sync_button: <SyncButton store={store} /> // Render sync button dynamically
        }))}
        showView={false}
        showEdit={true}
        showDelete={false}
        onEdit={(row) => navigate(`/pos-sync/${row.client_store_id}/fetch-update`)}

        customHeaderButtons={[
          {
            label: "Configure New POS",
            icon: "settings",
            onClick: () => {
              navigate("/pos-sync/configure");
            },
          },
        ]}
      />
    </>
  );
}

export default SyncList;
